import React, { useState, useEffect } from 'react';
import type { DiaryItem, Activity, SubType, Supplement, SupplementCatalog, ActivityType } from '../models/diary';
import { diaryService, ExistingActivity, ExistingSupplement } from '../services/diaryService';

interface DiaryItemCreateModalProps {
  open: boolean;
  onSave: (newItem: Partial<DiaryItem>) => void;
  onCancel: () => void;
  defaultDayOfWeek?: string;
  defaultItemType?: 'FitnessItem' | 'SupplementItem';
  editingItem?: DiaryItem | null; // If provided, modal is in edit mode
  mode?: 'create' | 'edit'; // Explicit mode flag
  patientUserId?: string | number; // User ID to fetch patient-specific activities/supplements
}

const DiaryItemCreateModal: React.FC<DiaryItemCreateModalProps> = ({ 
  open, 
  onSave, 
  onCancel, 
  defaultDayOfWeek = 'Monday',
  defaultItemType = 'FitnessItem',
  editingItem = null,
  mode = 'create',
  patientUserId
}) => {
  const isEditMode = mode === 'edit' || editingItem !== null;
  
  const [itemType, setItemType] = useState<'FitnessItem' | 'SupplementItem'>(
    editingItem?.type as 'FitnessItem' | 'SupplementItem' || defaultItemType
  );
  const [formData, setFormData] = useState({
    name: editingItem?.name || '',
    selectedDays: editingItem ? [editingItem.dayOfWeek] : [defaultDayOfWeek] as string[],
    timeOfDay: editingItem?.timeOfDay || 'Morning',
    subtype: editingItem?.subtype || 'gym' as SubType,
    order: editingItem?.order || 1,
    hidden: editingItem?.hidden || false,
    activities: editingItem?.activities || [] as Activity[],
    supplements: editingItem?.supplements || [] as Supplement[]
  });

  // State for existing items from database
  const [existingActivities, setExistingActivities] = useState<ExistingActivity[]>([]);
  const [existingSupplements, setExistingSupplements] = useState<ExistingSupplement[]>([]);
  const [supplementCatalog, setSupplementCatalog] = useState<SupplementCatalog[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [loadingExistingItems, setLoadingExistingItems] = useState(false);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  // State for activity selection mode
  const [activitySelectionMode, setActivitySelectionMode] = useState<'existing' | 'new'>('existing');
  const [selectedExistingActivityIds, setSelectedExistingActivityIds] = useState<number[]>([]);

  // State for supplement selection mode  
  const [supplementSelectionMode, setSupplementSelectionMode] = useState<'existing' | 'new'>('existing');
  const [selectedExistingSupplementIds, setSelectedExistingSupplementIds] = useState<number[]>([]);

  // Fetch existing items and catalogs when modal opens
  useEffect(() => {
    if (open && patientUserId) {
      const fetchExistingItems = async () => {
        setLoadingExistingItems(true);
        setLoadingCatalogs(true);
        try {
          const [activities, supplements, suppCatalog, actTypes] = await Promise.all([
            diaryService.getExistingActivities(patientUserId),
            diaryService.getExistingSupplements(patientUserId),
            diaryService.getSupplementCatalog(),
            diaryService.getActivityTypes()
          ]);
          setExistingActivities(activities);
          setExistingSupplements(supplements);
          setSupplementCatalog(suppCatalog);
          setActivityTypes(actTypes);
        } catch (error) {
          console.error('Error fetching existing items:', error);
        } finally {
          setLoadingExistingItems(false);
          setLoadingCatalogs(false);
        }
      };

      fetchExistingItems();
    }
  }, [open, patientUserId]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (isEditMode && editingItem) {
        // Edit mode: populate form with existing item data
        setItemType(editingItem.type as 'FitnessItem' | 'SupplementItem');
        setFormData({
          name: editingItem.name,
          selectedDays: [editingItem.dayOfWeek],
          timeOfDay: editingItem.timeOfDay,
          subtype: editingItem.subtype,
          order: editingItem.order,
          hidden: editingItem.hidden || false,
          activities: editingItem.activities || [],
          supplements: editingItem.supplements || []
        });
        // Initialize selected existing IDs for edit mode
        const existingActivityIds = (editingItem.activities || [])
          .filter(a => a.id)
          .map(a => a.id!);
        const existingSupplementIds = (editingItem.supplements || [])
          .filter(s => s.id)
          .map(s => s.id!);
        setSelectedExistingActivityIds(existingActivityIds);
        setSelectedExistingSupplementIds(existingSupplementIds);
      } else {
        // Create mode: reset form
        setItemType(defaultItemType);
        setFormData({
          name: '',
          selectedDays: [defaultDayOfWeek],
          timeOfDay: 'Morning',
          subtype: (defaultItemType === 'FitnessItem' ? 'gym' : 'supplementation') as SubType,
          order: 1,
          hidden: false,
          activities: [],
          supplements: []
        });
        setSelectedExistingActivityIds([]);
        setSelectedExistingSupplementIds([]);
      }
      // Reset selection states
      setActivitySelectionMode('existing');
      setSupplementSelectionMode('existing');
    }
  }, [open, defaultDayOfWeek, defaultItemType, isEditMode, editingItem]);

  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle day selection (multiple days)
  const handleDayToggle = (day: string) => {
    setFormData(prev => {
      const selectedDays = prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day];
      return { ...prev, selectedDays };
    });
  };

  // Activity management functions
  const handleActivityChange = (activityIndex: number, field: string, value: string | number) => {
    const updatedActivities = [...formData.activities];
    
    if (field === 'activityTypeId') {
      // When selecting activity type, save both ID and name
      const actType = activityTypes.find(t => t.id === value);
      if (actType) {
        updatedActivities[activityIndex] = {
          ...updatedActivities[activityIndex],
          activityType: actType.name,
          activityTypeId: actType.id
        };
      }
    } else {
      updatedActivities[activityIndex] = {
        ...updatedActivities[activityIndex],
        [field]: value
      };
    }
    
    setFormData(prev => ({ ...prev, activities: updatedActivities }));
  };

  const addActivity = () => {
    // Create a placeholder activity - catalog should be selected to pre-fill fields
    const newActivity: Partial<Activity> = {
      name: '',
      activityType: '',
      details: '',
      activityLevel: '',
      expectedDurationMin: 0
    };
    setFormData(prev => ({ 
      ...prev, 
      activities: [...prev.activities, newActivity as Activity] 
    }));
  };

  const removeActivity = (activityIndex: number) => {
    const updatedActivities = [...formData.activities];
    updatedActivities.splice(activityIndex, 1);
    setFormData(prev => ({ ...prev, activities: updatedActivities }));
  };

  // Supplement management functions
  const handleSupplementChange = (supplementIndex: number, field: string, value: string | number) => {
    const updatedSupplements = [...formData.supplements];
    
    if (field === 'catalogId') {
      // Handle catalog selection
      const catalog = supplementCatalog.find(c => c.id === value);
      if (catalog) {
        updatedSupplements[supplementIndex] = {
          ...updatedSupplements[supplementIndex],
          supplementCatalog: catalog
        };
      }
    } else if (field === 'description') {
      updatedSupplements[supplementIndex] = {
        ...updatedSupplements[supplementIndex],
        description: value as string
      };
    }
    
    setFormData(prev => ({ ...prev, supplements: updatedSupplements }));
  };

  const addSupplement = () => {
    // Create a placeholder supplement - catalog must be selected before it's valid
    const newSupplement: Partial<Supplement> = {
      description: ''
    };
    setFormData(prev => ({ 
      ...prev, 
      supplements: [...prev.supplements, newSupplement as Supplement] 
    }));
  };

  const removeSupplement = (supplementIndex: number) => {
    const updatedSupplements = [...formData.supplements];
    updatedSupplements.splice(supplementIndex, 1);
    setFormData(prev => ({ ...prev, supplements: updatedSupplements }));
  };

  // Helper functions for existing item selection
  const handleExistingActivitySelection = (activityId: number) => {
    const existingActivity = existingActivities.find(a => a.id === activityId);
    if (!existingActivity) return;

    // Convert existing activity to Activity format and add to selected IDs
    const activity: Activity = {
      id: existingActivity.id,
      name: existingActivity.name,
      activityType: existingActivity.activityType,
      details: existingActivity.details,
      activityLevel: existingActivity.activityLevel,
      expectedDurationMin: existingActivity.expectedDurationMin
    };

    setFormData(prev => ({ 
      ...prev, 
      activities: [...prev.activities, activity] 
    }));
    setSelectedExistingActivityIds(prev => [...prev, activityId]);
  };

  const handleExistingSupplementSelection = (supplementId: number) => {
    const existingSupplement = existingSupplements.find(s => s.id === supplementId);
    if (!existingSupplement) return;

    // Convert existing supplement to Supplement format and add to selected IDs
    const supplement: Supplement = {
      id: existingSupplement.id,
      description: existingSupplement.description,
      supplementCatalog: existingSupplement.supplementCatalog
    };

    setFormData(prev => ({ 
      ...prev, 
      supplements: [...prev.supplements, supplement] 
    }));
    setSelectedExistingSupplementIds(prev => [...prev, supplementId]);
  };

  const removeExistingActivity = (activityId: number) => {
    setFormData(prev => ({ 
      ...prev, 
      activities: prev.activities.filter(a => a.id !== activityId) 
    }));
    setSelectedExistingActivityIds(prev => prev.filter(id => id !== activityId));
  };

  const removeExistingSupplement = (supplementId: number) => {
    setFormData(prev => ({ 
      ...prev, 
      supplements: prev.supplements.filter(s => s.id !== supplementId) 
    }));
    setSelectedExistingSupplementIds(prev => prev.filter(id => id !== supplementId));
  };

  const handleSave = () => {
    // Validate that at least one day is selected
    if (formData.selectedDays.length === 0) {
      alert('Por favor selecciona al menos un día de la semana');
      return;
    }

    // Validate activities if FitnessItem
    if (itemType === 'FitnessItem' && formData.activities.length > 0) {
      const invalidActivities = formData.activities.filter(activity => {
        // Activity is valid if it has activityTypeId (new) OR activityType (existing)
        const hasTypeId = !!activity.activityTypeId;
        const hasTypeObject = activity.activityType && typeof activity.activityType === 'object';
        return !hasTypeId && !hasTypeObject;
      });
      if (invalidActivities.length > 0) {
        alert('Por favor selecciona el tipo de actividad para todas las actividades');
        return;
      }
    }

    // Validate supplements if SupplementItem
    if (itemType === 'SupplementItem' && formData.supplements.length > 0) {
      const invalidSupplements = formData.supplements.filter(supplement => !supplement.supplementCatalog);
      if (invalidSupplements.length > 0) {
        alert('Por favor selecciona el catálogo de suplemento para todos los suplementos');
        return;
      }
    }

    if (isEditMode && editingItem) {
      // Edit mode: update single item
      const updatedItem: Partial<DiaryItem> = {
        ...formData,
        id: editingItem.id,
        dayOfWeek: formData.selectedDays[0], // Use first selected day for edit
        type: itemType
      };
      // Remove selectedDays from the item since it's not part of DiaryItem interface
      const { selectedDays, ...itemWithoutSelectedDays } = updatedItem as any;
      onSave(itemWithoutSelectedDays);
    } else {
      // Create mode: create one DiaryItem for each selected day
      formData.selectedDays.forEach(day => {
        const newItem: Partial<DiaryItem> = {
          ...formData,
          dayOfWeek: day,
          type: itemType
        };
        // Remove selectedDays from the item since it's not part of DiaryItem interface
        const { selectedDays, ...itemWithoutSelectedDays } = newItem as any;
        onSave(itemWithoutSelectedDays);
      });
    }
  };

  if (!open) return null;

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.18)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 1000 
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 12, 
        padding: '32px 40px', 
        width: '100%',
        maxWidth: 560, 
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 32px rgba(0,0,0,0.13)' 
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 20, fontSize: 22, fontWeight: 600, color: '#1f2937' }}>
          {isEditMode ? `Editar ${itemType === 'FitnessItem' ? 'Ejercicio' : 'Suplemento'}` : 'Crear Elemento del Diario'}
        </h2>
        
        {/* Item Type Selection (only in create mode) */}
        {!isEditMode && (
          <label style={{ display: 'block', marginBottom: 18 }}>
            <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
              Tipo de Elemento:
            </span>
            <select 
              value={itemType} 
              onChange={e => setItemType(e.target.value as any)}
              style={{ 
                width: '100%', 
                padding: '11px 14px', 
                border: '1px solid #cbd5e1', 
                borderRadius: 6,
                fontSize: 15,
                outline: 'none',
                background: '#fff',
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
            >
              <option value="FitnessItem">Ejercicio</option>
              <option value="SupplementItem">Suplemento</option>
            </select>
          </label>
        )}

        {/* Basic Fields */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Nombre:
          </span>
          <input 
            value={formData.name} 
            onChange={e => handleFieldChange('name', e.target.value)} 
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box'
            }} 
            placeholder="Nombre del Item"
          />
        </label>

        {/* Days of week: checkboxes for create, dropdown for edit */}
        {isEditMode ? (
          <label style={{ display: 'block', marginBottom: 18 }}>
            <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
              Día de la semana:
            </span>
            <select 
              value={formData.selectedDays[0] || 'Monday'} 
              onChange={e => setFormData(prev => ({ ...prev, selectedDays: [e.target.value] }))}
              style={{ 
                width: '100%', 
                padding: '11px 14px', 
                border: '1px solid #cbd5e1', 
                borderRadius: 6,
                fontSize: 15,
                outline: 'none',
                background: '#fff',
                boxSizing: 'border-box',
                cursor: 'pointer'
              }}
            >
              <option value="Monday">Lunes</option>
              <option value="Tuesday">Martes</option>
              <option value="Wednesday">Miércoles</option>
              <option value="Thursday">Jueves</option>
              <option value="Friday">Viernes</option>
              <option value="Saturday">Sábado</option>
              <option value="Sunday">Domingo</option>
            </select>
          </label>
        ) : (
          <div style={{ marginBottom: 18 }}>
            <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
              Días de la semana:
            </span>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(2, 1fr)', 
              gap: 8, 
              border: '1px solid #ccc', 
              borderRadius: 4, 
              padding: 12 
            }}>
              {[
                { value: 'Monday', label: 'Lunes' },
                { value: 'Tuesday', label: 'Martes' },
                { value: 'Wednesday', label: 'Miércoles' },
                { value: 'Thursday', label: 'Jueves' },
                { value: 'Friday', label: 'Viernes' },
                { value: 'Saturday', label: 'Sábado' },
                { value: 'Sunday', label: 'Domingo' }
              ].map(day => (
                <label key={day.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input 
                    type="checkbox"
                    checked={formData.selectedDays.includes(day.value)}
                    onChange={() => handleDayToggle(day.value)}
                    style={{ marginRight: 6 }}
                  />
                  <span style={{ fontSize: 14 }}>{day.label}</span>
                </label>
              ))}
            </div>
            {formData.selectedDays.length === 0 && (
              <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                Selecciona al menos un día
              </div>
            )}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Momento del día:
          </span>
          <select 
            value={formData.timeOfDay} 
            onChange={e => handleFieldChange('timeOfDay', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              background: '#fff',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }}
          >
            <option value="Morning">Mañana</option>
            <option value="Afternoon">Tarde</option>
            <option value="Evening">Noche</option>
            <option value="Other">Otro</option>
          </select>
        </label>

        {/* Subtype based on item type */}
        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            {itemType === 'FitnessItem' ? 'Tipo de ejercicio:' : 'Tipo de suplemento:'}
          </span>
          <select 
            value={formData.subtype} 
            onChange={e => handleFieldChange('subtype', e.target.value)}
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              background: '#fff',
              boxSizing: 'border-box',
              cursor: 'pointer'
            }}
          >
            {itemType === 'FitnessItem' && (
              <>
                <option value="cardio">Cardio</option>
                <option value="gym">Gym</option>
                <option value="sport">Sport</option>
                <option value="swimming">Swimming</option>
                <option value="yoga">Yoga</option>
                <option value="pilates">Pilates</option>
                <option value="walking">Walking</option>
              </>
            )}
            {itemType === 'SupplementItem' && (
              <option value="supplementation">Suplementación</option>
            )}
          </select>
        </label>

        <label style={{ display: 'block', marginBottom: 18 }}>
          <span style={{ fontWeight: 600, marginBottom: 8, display: 'block', fontSize: 14, color: '#374151' }}>
            Orden:
          </span>
          <input 
            type="number" 
            value={formData.order} 
            onChange={e => handleFieldChange('order', parseInt(e.target.value))} 
            style={{ 
              width: '100%', 
              padding: '11px 14px', 
              border: '1px solid #cbd5e1', 
              borderRadius: 6,
              fontSize: 15,
              outline: 'none',
              boxSizing: 'border-box'
            }} 
            min="1" 
          />
        </label>

        <label style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <input 
            type="checkbox" 
            checked={formData.hidden} 
            onChange={e => handleFieldChange('hidden', e.target.checked)} 
            style={{ marginRight: 8, cursor: 'pointer' }} 
          />
          <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>Oculto</span>
        </label>


        {/* Activities Section (only for FitnessItem) */}
        {itemType === 'FitnessItem' && (
          <div style={{ marginBottom: 15 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Actividades:</span>
            </div>

            {/* Activity Selection Mode Toggle */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <input 
                  type="radio" 
                  name="activityMode" 
                  checked={activitySelectionMode === 'existing'} 
                  onChange={() => setActivitySelectionMode('existing')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Seleccionar de existentes</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input 
                  type="radio" 
                  name="activityMode" 
                  checked={activitySelectionMode === 'new'} 
                  onChange={() => setActivitySelectionMode('new')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Crear nueva actividad</span>
              </label>
            </div>

            {/* Existing Activities Selection */}
            {activitySelectionMode === 'existing' && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Seleccionar actividad:</span>
                  {loadingExistingItems ? (
                    <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando actividades...</div>
                  ) : (
                    <select 
                      onChange={e => e.target.value && handleExistingActivitySelection(parseInt(e.target.value))}
                      style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }}
                      value=""
                    >
                      <option value="">Seleccionar una actividad</option>
                      {(existingActivities || [])
                        .filter(activity => !selectedExistingActivityIds.includes(activity.id))
                        .map(activity => (
                          <option key={activity.id} value={activity.id}>
                            {activity.name} ({activity.activityLevel})
                          </option>
                        ))
                      }
                    </select>
                  )}
                </label>
              </div>
            )}

            {/* New Activity Creation */}
            {activitySelectionMode === 'new' && (
              <div style={{ marginBottom: 10 }}>
                <button 
                  type="button" 
                  onClick={addActivity}
                  style={{ 
                    background: '#38b2ac', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 4, 
                    padding: '4px 8px', 
                    fontSize: 12, 
                    cursor: 'pointer',
                    marginBottom: 8
                  }}
                >
                  + Agregar Nueva Actividad
                </button>
              </div>
            )}

            {/* Display Selected/Created Activities */}
            {formData.activities.map((activity, index) => {
              const isExisting = activity.id && selectedExistingActivityIds.includes(activity.id);
              return (
                <div key={activity.id || index} style={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 6, 
                  padding: 8, 
                  marginBottom: 8, 
                  backgroundColor: isExisting ? '#f0f9ff' : '#f9f9f9' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                      {isExisting ? `Actividad Existente: ${activity.name}` : `Actividad ${index + 1}`}
                    </span>
                    <button 
                      type="button" 
                      onClick={() => isExisting ? removeExistingActivity(activity.id!) : removeActivity(index)}
                      style={{ 
                        background: '#ef4444', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        padding: '2px 6px', 
                        fontSize: 12, 
                        cursor: 'pointer' 
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                  
                  {isExisting ? (
                    // Read-only display for existing activities
                    <div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Tipo:</strong> {typeof activity.activityType === 'object' ? activity.activityType.name : activity.activityType}
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Nivel:</strong> {activity.activityLevel}
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Duración:</strong> {activity.expectedDurationMin} min
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Detalles:</strong> {activity.details}
                      </div>
                    </div>
                  ) : (
                    // Editable fields for new activities
                    <>
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Tipo de actividad:</span>
                        {loadingCatalogs ? (
                          <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando tipos...</div>
                        ) : (
                          <select 
                            value={activity.activityTypeId || ''} 
                            onChange={e => e.target.value && handleActivityChange(index, 'activityTypeId', parseInt(e.target.value))}
                            style={{ 
                              width: '100%', 
                              marginTop: 2, 
                              padding: 4, 
                              border: '1px solid #ccc', 
                              borderRadius: 3, 
                              fontSize: 13 
                            }}
                          >
                            <option value="">Seleccionar tipo</option>
                            {(activityTypes || []).map(actType => (
                              <option key={actType.id} value={actType.id}>
                                {actType.name} - {actType.description}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Nombre:</span>
                        <input 
                          value={activity.name} 
                          onChange={e => handleActivityChange(index, 'name', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13 
                          }} 
                          placeholder="Ej: Carrera matutina"
                        />
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Nivel de intensidad:</span>
                        <select 
                          value={activity.activityLevel} 
                          onChange={e => handleActivityChange(index, 'activityLevel', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13 
                          }}
                        >
                          <option value="">Seleccionar nivel</option>
                          <option value="Low">Bajo</option>
                          <option value="Medium">Medio</option>
                          <option value="High">Alto</option>
                        </select>
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Duración esperada (min):</span>
                        <input 
                          type="number"
                          value={activity.expectedDurationMin} 
                          onChange={e => handleActivityChange(index, 'expectedDurationMin', parseInt(e.target.value) || 0)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13 
                          }} 
                        />
                      </label>
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Detalles:</span>
                        <textarea 
                          value={activity.details} 
                          onChange={e => handleActivityChange(index, 'details', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13, 
                            minHeight: 40, 
                            resize: 'vertical' 
                          }} 
                          placeholder="Detalles adicionales"
                        />
                      </label>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Supplements Section (only for SupplementItem) */}
        {itemType === 'SupplementItem' && (
          <div style={{ marginBottom: 15 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Suplementos:</span>
            </div>

            {/* Supplement Selection Mode Toggle */}
            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <input 
                  type="radio" 
                  name="supplementMode" 
                  checked={supplementSelectionMode === 'existing'} 
                  onChange={() => setSupplementSelectionMode('existing')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Seleccionar de existentes</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                <input 
                  type="radio" 
                  name="supplementMode" 
                  checked={supplementSelectionMode === 'new'} 
                  onChange={() => setSupplementSelectionMode('new')}
                  style={{ marginRight: 6 }}
                />
                <span style={{ fontSize: 14 }}>Crear nuevo suplemento</span>
              </label>
            </div>

            {/* Existing Supplements Selection */}
            {supplementSelectionMode === 'existing' && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Seleccionar suplemento:</span>
                  {loadingExistingItems ? (
                    <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando suplementos...</div>
                  ) : (
                    <select 
                      onChange={e => e.target.value && handleExistingSupplementSelection(parseInt(e.target.value))}
                      style={{ width: '100%', marginTop: 2, padding: 4, border: '1px solid #ccc', borderRadius: 3, fontSize: 13 }}
                      value=""
                    >
                      <option value="">Seleccionar un suplemento</option>
                      {(existingSupplements || [])
                        .filter(supplement => !selectedExistingSupplementIds.includes(supplement.id))
                        .map(supplement => (
                          <option key={supplement.id} value={supplement.id}>
                            {supplement.supplementCatalog?.name || 'Sin nombre'}
                          </option>
                        ))
                      }
                    </select>
                  )}
                </label>
              </div>
            )}

            {/* New Supplement Creation */}
            {supplementSelectionMode === 'new' && (
              <div style={{ marginBottom: 10 }}>
                <button 
                  type="button" 
                  onClick={addSupplement}
                  style={{ 
                    background: '#38b2ac', 
                    color: '#fff', 
                    border: 'none', 
                    borderRadius: 4, 
                    padding: '4px 8px', 
                    fontSize: 12, 
                    cursor: 'pointer',
                    marginBottom: 8
                  }}
                >
                  + Agregar Nuevo Suplemento
                </button>
              </div>
            )}

            {/* Display Selected/Created Supplements */}
            {formData.supplements.map((supplement, index) => {
              const isExisting = supplement.id && selectedExistingSupplementIds.includes(supplement.id);
              return (
                <div key={supplement.id || index} style={{ 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 6, 
                  padding: 8, 
                  marginBottom: 8, 
                  backgroundColor: isExisting ? '#f0f9ff' : '#f9f9f9' 
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                      {isExisting ? `Suplemento Existente: ${supplement.supplementCatalog?.name || 'Sin nombre'}` : `Suplemento ${index + 1}`}
                    </span>
                    <button 
                      type="button" 
                      onClick={() => isExisting ? removeExistingSupplement(supplement.id!) : removeSupplement(index)}
                      style={{ 
                        background: '#ef4444', 
                        color: '#fff', 
                        border: 'none', 
                        borderRadius: 4, 
                        padding: '2px 6px', 
                        fontSize: 12, 
                        cursor: 'pointer' 
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                  
                  {isExisting ? (
                    // Read-only display for existing supplements
                    <div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Nombre:</strong> {supplement.supplementCatalog?.name || 'N/A'}
                      </div>
                      <div style={{ fontSize: 13, marginBottom: 4 }}>
                        <strong>Descripción:</strong> {supplement.description}
                      </div>
                      {supplement.supplementCatalog?.purchaseUrl && (
                        <div style={{ fontSize: 13, marginBottom: 4 }}>
                          <strong>URL de compra:</strong> 
                          <a href={supplement.supplementCatalog.purchaseUrl} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 4, color: '#38b2ac' }}>
                            {supplement.supplementCatalog.purchaseUrl}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Editable fields for new supplements
                    <>
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Catálogo de suplementos:</span>
                        {loadingCatalogs ? (
                          <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Cargando catálogo...</div>
                        ) : (
                          <select 
                            value={supplement.supplementCatalog?.id || ''} 
                            onChange={e => handleSupplementChange(index, 'catalogId', parseInt(e.target.value))}
                            style={{ 
                              width: '100%', 
                              marginTop: 2, 
                              padding: 4, 
                              border: '1px solid #ccc', 
                              borderRadius: 3, 
                              fontSize: 13 
                            }}
                          >
                            <option value="">Seleccionar suplemento del catálogo</option>
                            {(supplementCatalog || []).map(catalog => (
                              <option key={catalog.id} value={catalog.id}>
                                {catalog.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                      
                      {supplement.supplementCatalog && (
                        <>
                          {supplement.supplementCatalog.description && (
                            <div style={{ fontSize: 12, color: '#374151', marginBottom: 4, padding: 6, backgroundColor: '#f0f9ff', borderRadius: 3, border: '1px solid #bfdbfe' }}>
                              <strong>Descripción:</strong> {supplement.supplementCatalog.description.length > 250 
                                ? `${supplement.supplementCatalog.description.substring(0, 250)}...` 
                                : supplement.supplementCatalog.description}
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: '#666', marginBottom: 4, padding: 4, backgroundColor: '#f0f9ff', borderRadius: 3 }}>
                            <strong>URL de compra:</strong>{' '}
                            <a href={supplement.supplementCatalog.purchaseUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#38b2ac' }}>
                              {supplement.supplementCatalog.purchaseUrl}
                            </a>
                          </div>
                        </>
                      )}
                      
                      <label style={{ display: 'block', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 500 }}>Descripción (instrucciones de uso):</span>
                        <textarea 
                          value={supplement.description} 
                          onChange={e => handleSupplementChange(index, 'description', e.target.value)}
                          style={{ 
                            width: '100%', 
                            marginTop: 2, 
                            padding: 4, 
                            border: '1px solid #ccc', 
                            borderRadius: 3, 
                            fontSize: 13, 
                            minHeight: 40, 
                            resize: 'vertical' 
                          }} 
                          placeholder="Ej: 1 al día por la mañana 10000U"
                        />
                      </label>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
          <button 
            onClick={onCancel} 
            style={{ 
              background: '#eee', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 18px', 
              fontWeight: 600, 
              cursor: 'pointer' 
            }}
          >
            Cancelar
          </button>
          <button 
            onClick={handleSave} 
            disabled={formData.selectedDays.length === 0}
            style={{ 
              background: formData.selectedDays.length === 0 ? '#ccc' : '#38b2ac', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '8px 18px', 
              fontWeight: 600, 
              cursor: formData.selectedDays.length === 0 ? 'not-allowed' : 'pointer',
              opacity: formData.selectedDays.length === 0 ? 0.6 : 1
            }}
          >
            {isEditMode 
              ? 'Guardar Cambios'
              : formData.selectedDays.length === 0 
                ? 'Selecciona días' 
                : formData.selectedDays.length === 1 
                  ? 'Crear Elemento' 
                  : `Crear ${formData.selectedDays.length} Elementos`
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiaryItemCreateModal;
